import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Star, 
  StarOff, 
  BookOpen, 
  Clock, 
  FileText,
  FolderOpen,
  SortAsc,
  SortDesc,
  Trash2,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { listEntries, createEntry, deleteEntry, searchEntries } from '../lib/entries';
import { listProjects } from '../../projects/lib/projects';
import type { Entry, Project } from '../../../shared/lib/supabase';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';

export function EntriesPage() {
  const navigate = useNavigate();
  const { isInTabContext, openEntryTab, projectId } = useTabNavigation();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [sortBy, setSortBy] = useState<'updated_at' | 'name' | 'word_count'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEntryName, setNewEntryName] = useState('');
  const [newEntryProject, setNewEntryProject] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<Entry | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projectFilterActive, setProjectFilterActive] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Check if we're in a project context
  const storedProjectId = sessionStorage.getItem('currentProjectId');

  useEffect(() => {
    // Only load on initial mount, not on selectedProject changes
    loadDataAndProject();
  }, []);

  useEffect(() => {
    // Handle selectedProject changes separately
    if (selectedProject !== '') {
      loadDataAndProject();
    }
  }, [selectedProject]);

  const loadDataAndProject = async () => {
    try {
      setLoading(true);
      const projectsData = await listProjects();
      setProjects(projectsData);
      
      // Load entries based on project context
      let entriesData: Entry[] = [];
      if (selectedProject) {
        // If user manually selected a project in dropdown
        entriesData = await listEntries(selectedProject);
      } else if (storedProjectId && !projectFilterActive) {
        // If coming from project context, filter by that project
        const project = projectsData.find(p => p.id === storedProjectId);
        if (project) {
          setCurrentProject(project);
          setProjectFilterActive(true);
          entriesData = await listEntries(storedProjectId);
        } else {
          // Project not found, show all
          entriesData = await listEntries();
        }
      } else if (!projectFilterActive) {
        // No filter active, show all
        entriesData = await listEntries();
      } else {
        // Keep current filter
        entriesData = await listEntries(currentProject?.id);
      }
      
      setEntries(entriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };
  
  const clearProjectFilter = () => {
    setProjectFilterActive(false);
    setCurrentProject(null);
    sessionStorage.removeItem('currentProjectId');
    loadDataAndProject();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadDataAndProject();
      return;
    }

    try {
      setLoading(true);
      const results = await searchEntries(searchQuery, selectedProject || undefined);
      setEntries(results);
    } catch (error) {
      console.error('Error searching entries:', error);
      toast.error('Failed to search entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery) return; // Don't do anything if searchQuery is empty
    
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleCreateEntry = async () => {
    if (isCreating) {
      return; // Prevent duplicate submissions
    }
    
    if (!newEntryName.trim()) {
      toast.error('Please enter an entry name');
      return;
    }

    // Use current project if in project context, otherwise require selection
    const projectId = (projectFilterActive && currentProject?.id) || newEntryProject;
    
    if (!projectId) {
      toast.error('Please select a project');
      return;
    }

    setIsCreating(true);
    
    try {
      const entryId = await createEntry({
        name: newEntryName.trim(),
        project_id: projectId
      });

      toast.success('Entry created successfully!');
      setShowCreateDialog(false);
      setNewEntryName('');
      setNewEntryProject('');
      
      // Open in new tab if in project context
      if (isInTabContext && projectId) {
        openEntryTab(entryId, newEntryName.trim());
      }
      
      // Refresh the entries list
      loadDataAndProject();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to create entry');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;

    try {
      await deleteEntry(entryToDelete.id);
      setEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      toast.success('Entry deleted successfully');
      setShowDeleteDialog(false);
      setEntryToDelete(null);
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteClick = (entry: Entry) => {
    setEntryToDelete(entry);
    setShowDeleteDialog(true);
  };

  const getSortedEntries = () => {
    return [...entries].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'word_count':
          comparison = a.word_count - b.word_count;
          break;
        case 'updated_at':
        default:
          comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const sortedEntries = getSortedEntries();

  return (
    <div className="min-h-full bg-white">
      <div className="px-6 py-8">
        <div className="w-full">
      {/* Main Container */}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Entries</h1>
              <p className="text-gray-600 mt-1">Digital lab notebook entries for experiments and protocols</p>
            </div>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="bg-gray-800 text-white px-4 py-2 rounded-sm hover:bg-gray-900 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Entry
            </button>
          </div>
        </div>


        {/* Search Bar - directly above table */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

      {/* Entries Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedEntries.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No entries found</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || selectedProject 
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first entry'
            }
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="bg-gray-800 text-white px-4 py-2 rounded-sm hover:bg-gray-900 transition-colors"
            >
              Create Entry
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 text-sm font-semibold text-gray-900">Name</th>
                <th className="text-left px-4 py-2.5 text-sm font-semibold text-gray-900">Project</th>
                <th className="text-center px-4 py-2.5 text-sm font-semibold text-gray-900">Word Count</th>
                <th className="text-left px-4 py-2.5 text-sm font-semibold text-gray-900">Last Updated</th>
                <th className="text-center px-4 py-2.5 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.slice(0, 10).map((entry, index) => (
                <tr 
                  key={entry.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                    index === sortedEntries.slice(0, 10).length - 1 ? 'border-b-0' : ''
                  }`}
                  onClick={() => {
                    if (isInTabContext) {
                      openEntryTab(entry.id, entry.name);
                    }
                  }}
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-100 rounded-sm flex items-center justify-center flex-shrink-0">
                        <FileText className="h-3 w-3 text-gray-600" />
                      </div>
                      <span className="font-medium text-gray-900 text-sm truncate max-w-xs">
                        {entry.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <FolderOpen className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-600 truncate max-w-xs">
                        {getProjectName(entry.project_id)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-100 text-gray-800">
                      {entry.word_count.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span className="text-sm">{formatRelativeTime(entry.updated_at)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(entry);
                        }}
                        className="p-1.5 rounded-sm hover:bg-red-50 transition-colors group"
                        title="Delete entry"
                      >
                        <Trash2 className="h-4 w-4 text-gray-400 group-hover:text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Show message if there are more than 10 entries */}
          {sortedEntries.length > 10 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Showing 10 of {sortedEntries.length} entries
              </p>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Create Entry Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Entry</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateEntry();
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entry Name *
                  </label>
                  <input
                    type="text"
                    value={newEntryName}
                    onChange={(e) => setNewEntryName(e.target.value)}
                    placeholder="My Lab Entry"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>

                {/* Only show project selector if not in a project context */}
                {!(projectFilterActive && currentProject?.id) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project *
                    </label>
                    <select
                      value={newEntryProject}
                      onChange={(e) => setNewEntryProject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewEntryName('');
                    setNewEntryProject('');
                    setIsCreating(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newEntryName.trim() || (!(projectFilterActive && currentProject?.id) && !newEntryProject)}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Creating...' : 'Create Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && entryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-red-600">Delete Entry</h2>
            
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete "<strong>{entryToDelete.name}</strong>"? 
              This action cannot be undone.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
              <p className="text-red-800 text-sm">
                ⚠️ <strong>Warning:</strong> This entry will be permanently deleted.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setEntryToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}